import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
} from "geojson";
import type { PathOptions, LeafletMouseEvent } from "leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { cn } from "@/lib/utils";
import { isOnline, setupOnlineOfflineListeners } from "@/utils/networkStatus";
import { OfflineBasemap } from "./OfflineBasemap";
import type { MapStyle } from "./mapStyles";
import {
  DEFAULT_POINT_STYLE,
  getFeatureStateStyle,
  applyHoverEffect,
  type FeatureState,
  type FeatureProgress,
} from "./mapStyles";

const DEFAULT_CENTER: [number, number] = [20, 0];
const DEFAULT_ZOOM = 2;
const DEFAULT_MAP_HEIGHT = 420;

// Camera animation constants
const FIT_BOUNDS_DELAY_MS = 100; // Delay before camera starts moving when data loads
const FIT_BOUNDS_PADDING = [32, 32] as [number, number]; // Padding for fitBounds
const FIT_BOUNDS_DURATION_SEC = 1.2; // Duration of the fit bounds animation (in seconds)

// Map style configurations
type TileLayerConfig = {
  baseUrl: string;
  labelsUrl?: string;
  labelsOpacity?: number;
  attribution: string;
};

const MAP_STYLE_CONFIGS: Record<MapStyle, TileLayerConfig> = {
  light: {
    baseUrl: "https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png",
    labelsUrl: "https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png",
    labelsOpacity: 0.55,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
  dark: {
    baseUrl: "https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png",
    labelsUrl: "https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png",
    labelsOpacity: 0.55,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
  voyager: {
    baseUrl: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png",
    labelsUrl: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png",
    labelsOpacity: 0.55,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
  satellite: {
    baseUrl: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    labelsUrl: "https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png",
    labelsOpacity: 0.75,
    attribution: '&copy; <a href="https://www.esri.com/">Esri</a>',
  },
  osm: {
    baseUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
};

export interface BaseMapProps {
  data: FeatureCollection<Geometry, GeoJsonProperties> | null;
  className?: string;
  mapHeight?: number | string;
  onMapReady?: (map: L.Map) => void;
  onLayerReady?: (layer: L.GeoJSON) => void;
  children?: React.ReactNode;

  // Display options
  hideLabels?: boolean;
  mapStyle?: MapStyle;
  disableAutoFitBounds?: boolean;
  initialZoom?: number;

  // Styling - Choose ONE approach:
  // 1. Use semantic state system (recommended for consistency):
  getFeatureState?: (feature: Feature<Geometry, GeoJsonProperties>) => FeatureState;
  getFeatureProgress?: (feature: Feature<Geometry, GeoJsonProperties>) => FeatureProgress | undefined;
  enableHoverEffect?: boolean; // Default: true

  // 2. OR use custom style function (for special cases):
  getFeatureStyle?: (feature: Feature<Geometry, GeoJsonProperties>) => PathOptions;

  // Event handlers
  onFeatureClick?: (feature: Feature<Geometry, GeoJsonProperties>, event: LeafletMouseEvent) => void;
  onFeatureMouseOver?: (feature: Feature<Geometry, GeoJsonProperties>, event: LeafletMouseEvent) => void;
  onFeatureMouseOut?: (feature: Feature<Geometry, GeoJsonProperties>, event: LeafletMouseEvent) => void;

  // Tooltip
  showTooltip?: boolean;
  renderTooltipContent?: (feature: Feature<Geometry, GeoJsonProperties>) => React.ReactNode;
}

/**
 * BaseMap - Core Leaflet infrastructure
 *
 * Provides:
 * - MapContainer with standard tile layers
 * - Consistent styling and dimensions
 * - Refs to map and layer instances
 * - Auto-fitting to bounds
 * - Feature styling via getFeatureStyle prop
 * - Event handling (click, hover, mouseout)
 * - Tooltip display with custom content
 */
export function BaseMap({
  data,
  className,
  mapHeight,
  onMapReady,
  onLayerReady,
  children,
  hideLabels = false,
  mapStyle = "light",
  disableAutoFitBounds = false,
  initialZoom,
  getFeatureState,
  getFeatureProgress,
  enableHoverEffect = true,
  getFeatureStyle,
  onFeatureClick,
  onFeatureMouseOver,
  onFeatureMouseOut,
  showTooltip = false,
  renderTooltipContent,
}: BaseMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.GeoJSON | null>(null);
  const [layerReady, setLayerReady] = useState(false);
  const hasFitBounds = useRef(false); // Track if we've fitted bounds already

  // Use Canvas renderer for better performance with many features
  const canvasRenderer = useRef<L.Canvas | null>(null);
  if (!canvasRenderer.current) {
    canvasRenderer.current = L.canvas({ padding: 0.5 });
  }

  // Create unified style function
  // Priority: getFeatureStyle (custom) > getFeatureState (semantic) > default
  const createFeatureStyle = useCallback(
    (feature: Feature<Geometry, GeoJsonProperties>): PathOptions => {
      // If custom style function provided, use it
      if (getFeatureStyle) {
        return getFeatureStyle(feature);
      }

      // Otherwise use semantic state system
      const state = getFeatureState ? getFeatureState(feature) : "default";
      const progress = getFeatureProgress ? getFeatureProgress(feature) : undefined;
      const isPoint = feature.geometry.type === "Point" || feature.geometry.type === "MultiPoint";

      return getFeatureStateStyle(state, progress, isPoint);
    },
    [getFeatureStyle, getFeatureState, getFeatureProgress]
  );

  // Online/offline state
  const [online, setOnline] = useState(() => isOnline());

  // Set up online/offline listeners
  useEffect(() => {
    const cleanup = setupOnlineOfflineListeners(
      () => setOnline(true),
      () => setOnline(false)
    );
    return cleanup;
  }, []);

  // Tooltip state
  const [tooltip, setTooltip] = useState<{
    feature: Feature<Geometry, GeoJsonProperties>;
    position: { x: number; y: number };
  } | null>(null);

  const resolvedHeight = useMemo(() => {
    if (typeof mapHeight === "number") {
      return `${mapHeight}px`;
    }
    if (typeof mapHeight === "string" && mapHeight.trim().length > 0) {
      return mapHeight;
    }
    return `${DEFAULT_MAP_HEIGHT}px`;
  }, [mapHeight]);

  // Get tile layer configuration for selected map style
  const tileConfig = useMemo(() => MAP_STYLE_CONFIGS[mapStyle], [mapStyle]);

  // Track feature index for generating unique IDs
  const featureIndexRef = useRef(0);

  // Store latest event handlers in refs to avoid stale closures
  const onFeatureClickRef = useRef(onFeatureClick);
  const onFeatureMouseOverRef = useRef(onFeatureMouseOver);
  const onFeatureMouseOutRef = useRef(onFeatureMouseOut);

  // Update refs when handlers change
  useEffect(() => {
    onFeatureClickRef.current = onFeatureClick;
    onFeatureMouseOverRef.current = onFeatureMouseOver;
    onFeatureMouseOutRef.current = onFeatureMouseOut;
  }, [onFeatureClick, onFeatureMouseOver, onFeatureMouseOut]);

  // Reset feature index and layer ready state when data changes
  useEffect(() => {
    featureIndexRef.current = 0;
    setLayerReady(false);
    hasFitBounds.current = false; // Reset here too, before layerReady changes
  }, [data]);

  // Custom point rendering function - render Points as CircleMarkers
  // This ensures points work with the same interaction system as polygons
  const pointToLayer = useCallback(
    (feature: Feature<Geometry, GeoJsonProperties>, latlng: L.LatLng): L.CircleMarker => {
      // Create a CircleMarker (which extends L.Path, so event handlers will work)
      // Use unified style function
      const style = createFeatureStyle(feature);
      const marker = L.circleMarker(latlng, {
        ...DEFAULT_POINT_STYLE,
        ...style,
      });

      return marker;
    },
    [createFeatureStyle],
  );

  // Create stable onEachFeature callback
  const onEachFeature = useCallback(
    (feature: Feature<Geometry, GeoJsonProperties>, layer: L.Layer) => {
      if (!(layer instanceof L.Path)) return;

      // Store feature ID on the layer for parent components to reference
      const featureId = feature.id !== undefined
        ? String(feature.id)
        : `feature-${featureIndexRef.current}`;
      (layer as any)._featureId = featureId;
      featureIndexRef.current++;

      layer.on({
        click: (e: LeafletMouseEvent) => {
          if (onFeatureClickRef.current) {
            onFeatureClickRef.current(feature, e);
          }
        },
        mouseover: (e: LeafletMouseEvent) => {
          // Apply hover effect if enabled
          if (enableHoverEffect) {
            const baseStyle = createFeatureStyle(feature);
            const hoverStyle = applyHoverEffect(baseStyle);
            layer.setStyle(hoverStyle);
          }

          // Call custom handler (if provided)
          if (onFeatureMouseOverRef.current) {
            onFeatureMouseOverRef.current(feature, e);
          }

          // Show tooltip if enabled
          if (showTooltip && mapRef.current) {
            const containerPoint = mapRef.current.latLngToContainerPoint(e.latlng);
            setTooltip({
              feature,
              position: { x: containerPoint.x, y: containerPoint.y },
            });
          }
        },
        mouseout: (e: LeafletMouseEvent) => {
          // Reset to base style
          if (enableHoverEffect) {
            const baseStyle = createFeatureStyle(feature);
            layer.setStyle(baseStyle);
          }

          // Call custom handler (if provided)
          if (onFeatureMouseOutRef.current) {
            onFeatureMouseOutRef.current(feature, e);
          }

          // Hide tooltip
          if (showTooltip) {
            setTooltip(null);
          }
        },
      });
    },
    [showTooltip, enableHoverEffect, createFeatureStyle],
  );

  // Notify parent when map is ready
  useEffect(() => {
    if (mapRef.current && onMapReady) {
      onMapReady(mapRef.current);
    }
  }, [onMapReady]);

  // Notify parent when layer is ready
  useEffect(() => {
    if (layerRef.current && onLayerReady) {
      onLayerReady(layerRef.current);
    }
  }, [onLayerReady]);

  // Auto-fit to bounds when data changes and layer is ready
  useEffect(() => {
    if (disableAutoFitBounds || !mapRef.current || !layerRef.current || !data || !layerReady) {
      return;
    }

    if (hasFitBounds.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const bounds = layerRef.current?.getBounds();

      if (bounds && bounds.isValid() && mapRef.current) {
        // Use fitBounds instead of flyToBounds for instant positioning
        mapRef.current.fitBounds(bounds, {
          padding: FIT_BOUNDS_PADDING,
          animate: false, // Disable animation for faster initial render
          duration: 0,
        });
        hasFitBounds.current = true;

        // Force a map invalidateSize and redraw after fitBounds
        // This fixes the "zoom to make it work" issue
        setTimeout(() => {
          if (mapRef.current) {
            mapRef.current.invalidateSize();
            // Force the map to redraw by slightly adjusting the zoom
            const currentZoom = mapRef.current.getZoom();
            mapRef.current.setZoom(currentZoom, { animate: false });
          }
        }, 50);
      }
    }, FIT_BOUNDS_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [data, layerReady, disableAutoFitBounds]);

  // Reset hasFitBounds when data changes (new dataset)
  useEffect(() => {
    hasFitBounds.current = false;
  }, [data]);

  return (
    <div
      className={cn(
        "map-container relative overflow-hidden rounded-3xl shadow-xl",
        className,
      )}
    >
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={initialZoom ?? DEFAULT_ZOOM}
        scrollWheelZoom
        style={{ height: resolvedHeight }}
        className="w-full"
        ref={(instance) => {
          mapRef.current = instance;
        }}
        worldCopyJump
        attributionControl={false}
      >
        {/* Online: Use tile layers based on selected style */}
        {online && (
          <>
            <TileLayer
              url={tileConfig.baseUrl}
              maxZoom={18}
              subdomains={["a", "b", "c", "d"]}
              attribution={tileConfig.attribution}
            />
            {!hideLabels && tileConfig.labelsUrl && (
              <TileLayer
                url={tileConfig.labelsUrl}
                maxZoom={18}
                subdomains={["a", "b", "c", "d"]}
                opacity={tileConfig.labelsOpacity ?? 0.55}
              />
            )}
          </>
        )}

        {/* Offline: Use simple world outline */}
        <OfflineBasemap show={!online} />

        {data && (
          <GeoJSON
            key={data.features.length}
            data={data}
            ref={(instance) => {
              layerRef.current = instance;
              if (instance) {
                setLayerReady(true);
              }
            }}
            style={createFeatureStyle as any}
            pointToLayer={pointToLayer}
            onEachFeature={onEachFeature}
            renderer={canvasRenderer.current || undefined}
          />
        )}
        {children}
      </MapContainer>
      <div className="pointer-events-none absolute inset-0 rounded-3xl border border-white/40 mix-blend-overlay" />

      {/* Tooltip */}
      {showTooltip && tooltip && renderTooltipContent && (() => {
        const content = renderTooltipContent(tooltip.feature);
        if (!content) return null;

        return (
          <div
            className="pointer-events-none absolute z-tooltip rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs shadow-lg"
            style={{
              left: tooltip.position.x + 12,
              top: tooltip.position.y + 12,
            }}
          >
            {content}
          </div>
        );
      })()}
    </div>
  );
}
